import { zeroErrorMessage } from "@/lib/zero-error";
import { useState } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import {
  Tables,
  TablesInsert,
  TablesUpdate,
  Database,
} from "@/integrations/neon/types";
import { generateUUID } from "@/lib/utils";
import { toast } from "sonner";

export type Person = Tables<"people">;
export type PersonInsert = TablesInsert<"people">;
export type PersonUpdate = TablesUpdate<"people">;
export type PersonType = Database["public"]["Enums"]["person_type"];

export type PersonWithCompany = Person & {
  companies: { name: string } | null;
  person_allergy_tags?: PersonAllergyTag[];
};

export type PersonAllergyTag = {
  id: string;
  person_id: string;
  allergy_tag_id: string;
  created_at: string;
  allergy_tag?: {
    id: string;
    name: string;
    created_at: string;
  };
};

type ZeroPersonRow = {
  id: string;
  userID?: string | null;
  companyID?: string | null;
  firstName: string;
  lastName: string;
  number?: string | null;
  function?: string | null;
  phone?: string | null;
  email?: string | null;
  personType: PersonType;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  deletedAt?: number | string | null;
  version?: number | null;
  isAdmin?: boolean | null;
  clockifyUserID?: string | null;
  rentmanID?: number | null;
  allergies?: string | null;
  remarks?: string | null;
  notes?: string | null;
  allergiesNotes?: string | null;
  unityUsername?: string | null;
  companies?: { name: string } | null;
  personAllergyTags?: {
    id: string;
    personID: string;
    allergyTagID: string;
    createdAt?: number | string | null;
    allergyTag?: {
      id: string;
      name: string;
      createdAt?: number | string | null;
    } | null;
  }[];
};

type MutationOptions<T> = { onSuccess?: (data: T) => void };
type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};

function getPersonMutationErrorMessage(error: Error) {
  if (
    error.message.includes("idx_people_unity_username_unique") ||
    error.message.includes("people_unity_username")
  ) {
    return "Unity username must be unique.";
  }

  return error.message;
}

function normalizeFilter(filter?: PersonType | PersonType[]) {
  if (!filter) return undefined;
  return Array.isArray(filter) ? [...filter].sort() : [filter];
}

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyNullableTimestamp(value: number | string | null | undefined) {
  return value ? toLegacyTimestamp(value) : null;
}

function toLegacyPerson(row: ZeroPersonRow): PersonWithCompany {
  return {
    id: row.id,
    user_id: row.userID ?? null,
    company_id: row.companyID ?? null,
    first_name: row.firstName,
    last_name: row.lastName,
    number: row.number ?? null,
    function: row.function ?? null,
    phone: row.phone ?? null,
    email: row.email ?? null,
    person_type: row.personType,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    deleted_at: toLegacyNullableTimestamp(row.deletedAt),
    version: row.version ?? null,
    is_admin: row.isAdmin ?? false,
    clockify_user_id: row.clockifyUserID ?? null,
    rentman_id: row.rentmanID ?? null,
    allergies: row.allergies ?? null,
    remarks: row.remarks ?? null,
    notes: row.notes ?? null,
    allergies_notes: row.allergiesNotes ?? null,
    unity_username: row.unityUsername ?? null,
    companies: row.companies ? { name: row.companies.name } : null,
    person_allergy_tags: (row.personAllergyTags ?? []).map((assignment) => ({
      id: assignment.id,
      person_id: assignment.personID,
      allergy_tag_id: assignment.allergyTagID,
      created_at: toLegacyTimestamp(assignment.createdAt),
      allergy_tag: assignment.allergyTag
        ? {
            id: assignment.allergyTag.id,
            name: assignment.allergyTag.name,
            created_at: toLegacyTimestamp(assignment.allergyTag.createdAt),
          }
        : undefined,
    })),
  };
}

function toZeroPersonInput(person: PersonInsert, id: string) {
  return {
    id,
    companyID: person.company_id ?? null,
    firstName: person.first_name,
    lastName: person.last_name,
    number: person.number ?? null,
    function: person.function ?? null,
    phone: person.phone ?? null,
    email: person.email ?? null,
    personType: person.person_type ?? "contact",
    ...(person.is_admin !== undefined ? { isAdmin: person.is_admin } : {}),
    clockifyUserID: person.clockify_user_id ?? null,
    rentmanID: person.rentman_id ?? null,
    allergies: person.allergies ?? null,
    remarks: person.remarks ?? null,
    notes: person.notes ?? null,
    allergiesNotes: person.allergies_notes ?? null,
    unityUsername: person.unity_username ?? null,
  };
}

function toZeroPersonUpdate(id: string, updates: PersonUpdate) {
  return {
    id,
    ...(updates.company_id !== undefined
      ? { companyID: updates.company_id }
      : {}),
    ...(updates.first_name !== undefined
      ? { firstName: updates.first_name }
      : {}),
    ...(updates.last_name !== undefined ? { lastName: updates.last_name } : {}),
    ...(updates.number !== undefined ? { number: updates.number } : {}),
    ...(updates.function !== undefined ? { function: updates.function } : {}),
    ...(updates.phone !== undefined ? { phone: updates.phone } : {}),
    ...(updates.email !== undefined ? { email: updates.email } : {}),
    ...(updates.person_type !== undefined
      ? { personType: updates.person_type }
      : {}),
    ...(updates.is_admin !== undefined ? { isAdmin: updates.is_admin } : {}),
    ...(updates.clockify_user_id !== undefined
      ? { clockifyUserID: updates.clockify_user_id }
      : {}),
    ...(updates.rentman_id !== undefined
      ? { rentmanID: updates.rentman_id }
      : {}),
    ...(updates.allergies !== undefined
      ? { allergies: updates.allergies }
      : {}),
    ...(updates.remarks !== undefined ? { remarks: updates.remarks } : {}),
    ...(updates.notes !== undefined ? { notes: updates.notes } : {}),
    ...(updates.allergies_notes !== undefined
      ? { allergiesNotes: updates.allergies_notes }
      : {}),
    ...(updates.unity_username !== undefined
      ? { unityUsername: updates.unity_username }
      : {}),
  };
}

async function reportServerMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

export function usePeople(
  filter?: PersonType | PersonType[],
  includeDeleted = false,
) {
  const normalizedFilter = normalizeFilter(filter);
  const [data, details] = useQuery(
    appQueries.people.list({
      personTypes: normalizedFilter,
      includeDeleted,
    }),
  );
  const people = ((data ?? []) as ZeroPersonRow[]).map(toLegacyPerson);

  return {
    data: people,
    isLoading: details.type !== "complete" && people.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function usePerson(id: string | undefined) {
  const query = id ? appQueries.people.byID({ id }) : undefined;
  const [data, details] = useQuery(query, { enabled: !!id });

  return {
    data: data ? toLegacyPerson(data as ZeroPersonRow) : null,
    isLoading: !!id && details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreatePerson() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (person: PersonInsert) => {
    const input = toZeroPersonInput(person, generateUUID());
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.people.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Person create was rejected",
      );
      return toLegacyPerson({ ...input, createdAt: "", updatedAt: "" });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      person: PersonInsert,
      options?: MutationOptions<PersonWithCompany>,
    ) => {
      void run(person)
        .then((data) => {
          toast.success("Person created successfully");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error(
            "Failed to create person: " + getPersonMutationErrorMessage(error),
          ),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdatePerson() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    updates,
  }: {
    id: string;
    updates: PersonUpdate;
  }) => {
    const input = toZeroPersonUpdate(id, updates);
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.people.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Person update was rejected",
      );
      return toLegacyPerson({
        ...input,
        firstName: input.firstName ?? "",
        lastName: input.lastName ?? "",
        personType: input.personType ?? "contact",
        createdAt: "",
        updatedAt: "",
      });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: { id: string; updates: PersonUpdate },
      options?: MutationOptions<PersonWithCompany>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success("Person updated successfully");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error(
            "Failed to update person: " + getPersonMutationErrorMessage(error),
          ),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useSoftDeletePerson() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (id: string) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.people.softDelete({ id }));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Person delete was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (id: string, options?: { onSuccess?: () => void }) => {
      void run(id)
        .then(() => {
          toast.success("Person moved to trash");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to delete person: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useRestorePerson() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (id: string) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.people.restore({ id }));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Person restore was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (id: string, options?: { onSuccess?: () => void }) => {
      void run(id)
        .then(() => {
          toast.success("Person restored");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to restore person: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
